import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues } from '../../SpaPackageForm.schema';

type Guest = SpaPackageFormValues['guests'][number];
type SelectionData = NonNullable<NonNullable<Guest['configurationGroups']>[string][number]>;

type UseConfigGroupHandlersParams = {
  form: UseFormReturn<SpaPackageFormValues>;
  configGroupsNoExtras: ConfigurationGroupType[];
};

function getRequiredGroup(configGroupsNoExtras: ConfigurationGroupType[], panelIndex: number) {
  return configGroupsNoExtras.filter((cg) => cg.isRequired)[panelIndex];
}

function updateGuestConfigGroup(
  form: UseFormReturn<SpaPackageFormValues>,
  guestIndex: number,
  groupKey: string,
  updater: (
    currentSelections: (SelectionData | null | undefined)[]
  ) => (SelectionData | null | undefined)[]
) {
  const currentGuests = form.getValues('guests') || [];
  const updatedGuests = currentGuests.map((guest, idx) => {
    if (idx !== guestIndex) return guest;
    const configGroups = guest.configurationGroups || {};
    const currentSelections = configGroups[groupKey] || [];
    return {
      ...guest,
      configurationGroups: {
        ...configGroups,
        [groupKey]: updater(currentSelections),
      },
    };
  });
  form.setValue('guests', updatedGuests as SpaPackageFormValues['guests'], {
    shouldValidate: true,
  });
}

export function useConfigGroupHandlers({
  form,
  configGroupsNoExtras,
}: UseConfigGroupHandlersParams) {
  const handleAllOptionsTimeslotChange = useCallback(
    (guestIndex: number, panelIndex: number, optionId: string, timeslotValue: string) => {
      const group = getRequiredGroup(configGroupsNoExtras, panelIndex);
      if (!group) return;
      const groupKey = `group_${group.id}`;
      const optionIdx = group.options.findIndex((o) => String(o.id) === optionId);
      if (optionIdx === -1) return;

      const endTime = group.options[optionIdx]?.timeslots?.find(
        (ts) => ts.startTime === timeslotValue
      )?.endTime;

      updateGuestConfigGroup(form, guestIndex, groupKey, (selections) => {
        const updated = [...selections];
        updated[optionIdx] = {
          configurationGroupId: group.id,
          optionId: Number(optionId),
          isSelected: true,
          timeslot: endTime ? { startTime: timeslotValue, endTime } : null,
        };
        return updated;
      });
    },
    [form, configGroupsNoExtras]
  );

  const handleSingleOptionSelect = useCallback(
    (guestIndex: number, panelIndex: number, optionId: string) => {
      const group = getRequiredGroup(configGroupsNoExtras, panelIndex);
      if (!group) return;
      const groupKey = `group_${group.id}`;

      updateGuestConfigGroup(form, guestIndex, groupKey, () =>
        group.options.map((opt) => ({
          configurationGroupId: group.id,
          optionId: opt.id,
          isSelected: String(opt.id) === optionId,
          timeslot: null,
        }))
      );
    },
    [form, configGroupsNoExtras]
  );

  const handleSingleOptionTimeslotChange = useCallback(
    (guestIndex: number, panelIndex: number, timeslotValue: string) => {
      const group = getRequiredGroup(configGroupsNoExtras, panelIndex);
      if (!group) return;
      const groupKey = `group_${group.id}`;

      const currentGuests = form.getValues('guests') || [];
      const guest = currentGuests[guestIndex];
      const currentSelections = guest?.configurationGroups?.[groupKey] || [];
      const selectedIdx = currentSelections.findIndex((s) => s?.isSelected);
      if (selectedIdx === -1 || selectedIdx == null) return;

      updateGuestConfigGroup(form, guestIndex, groupKey, (selections) => {
        const selectedOpt = group.options[selectedIdx];
        const endTime = selectedOpt?.timeslots?.find(
          (ts) => ts.startTime === timeslotValue
        )?.endTime;

        const current = selections[selectedIdx];
        if (!current) return selections;
        const updated = [...selections];
        updated[selectedIdx] = {
          configurationGroupId: current.configurationGroupId,
          optionId: current.optionId,
          isSelected: current.isSelected,
          timeslot: endTime ? { startTime: timeslotValue, endTime } : null,
        };
        return updated;
      });
    },
    [form, configGroupsNoExtras]
  );

  return {
    handleAllOptionsTimeslotChange,
    handleSingleOptionSelect,
    handleSingleOptionTimeslotChange,
  };
}
